// src/utils/locationUtils.js

import { getDistance } from 'geolib';

const OBFUSCATION_RADIUS_METERS = 400;

export const LocationFormat = {
  standardize: (coordinates) => {
    if (!coordinates) return null;
    
    const lat = coordinates.lat || coordinates.latitude;
    const lng = coordinates.lng || coordinates.longitude;
    
    if (typeof lat !== 'number' || typeof lng !== 'number') {
      return null;
    }

    return {
      lat,
      lng
    };
  },

  calculateDistance: (coords1, coords2) => {
    const point1 = LocationFormat.standardize(coords1);
    const point2 = LocationFormat.standardize(coords2);
    
    if (!point1 || !point2) return null;

    return getDistance(
      { latitude: point1.lat, longitude: point1.lng },
      { latitude: point2.lat, longitude: point2.lng }
    );
  },

  formatDistance: (meters) => {
    if (!meters) return '';
    const miles = (meters / 1609.34).toFixed(1);
    return miles === "0.0" ? 
      'Less than a mile away' : 
      `${miles} ${miles === "1.0" ? 'mile' : 'miles'} away`;
  },

  obfuscate: (coordinates) => {
    if (!coordinates) return null;
    
    const point = LocationFormat.standardize(coordinates);
    if (!point) return null;
    
    // Random angle and distance
    const angle = Math.random() * 2 * Math.PI;
    const distance = Math.random() * OBFUSCATION_RADIUS_METERS;
    
    // Calculate offset in degrees (rough approximation)
    const latOffset = (distance * Math.cos(angle)) / 111320;
    const lngOffset = (distance * Math.sin(angle)) / (111320 * Math.cos(point.lat * Math.PI / 180));
    
    return {
      lat: point.lat + latOffset,
      lng: point.lng + lngOffset,
      isObfuscated: true
    };
  },
  
  // ADD: Validate address format
  validateAddress: (address) => {
    if (!address || address.trim().length < 5) return false;
    
    // Check if it's coordinates
    const coordsMatch = address.match(/^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/);
    if (coordsMatch) {
      const lat = parseFloat(coordsMatch[1]);
      const lng = parseFloat(coordsMatch[2]);
      return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
    }
    
    // Take Off address validation (has some alphanumeric)
    return /[a-zA-Z0-9]{3,}/.test(address);
  },
  
  // ADD: Distance cache
  _distanceCache: new Map(),
  
  getCachedDistance: (coords1, coords2) => {
    const key = `${coords1.lat},${coords1.lng}-${coords2.lat},${coords2.lng}`;
    
    if (LocationFormat._distanceCache.has(key)) {
      return LocationFormat._distanceCache.get(key);
    }
    
    const distance = LocationFormat.calculateDistance(coords1, coords2);
    LocationFormat._distanceCache.set(key, distance);
    
    // Clear cache if too large
    if (LocationFormat._distanceCache.size > 1000) {
      LocationFormat._distanceCache.clear();
    }
    
    return distance;
  },

  // ADD: Privacy level helpers
  getLocationByPrivacy: (item, privacyLevel = 'neighborhood') => {
    if (!item.coordinates) return null;

    switch(privacyLevel) {
      case 'exact':
        return null; // Never show exact publicly
      case 'neighborhood':
        return LocationFormat.obfuscate(item.coordinates);
      case 'city':
        return null; // Would need geocoding service
      default:
        return LocationFormat.obfuscate(item.coordinates);
    }
  }
};

// ─── IP location fetch (shared by ShopPage & WelcomePage) ────────────────────

const IP_PROVIDERS = [
  { url: 'https://ipapi.co/json/',                 parse: d => (!d||d.error||!d.city) ? null : { city: d.city, state: d.region,  country: d.country_name } },
  { url: 'https://ipwho.is/',                      parse: d => (!d?.success||!d.city) ? null : { city: d.city, state: d.region,  country: d.country      } },
  { url: 'https://get.geojs.io/v1/ip/geo.json',   parse: d => (!d?.city)             ? null : { city: d.city, state: d.region,  country: d.country      } },
];

/** Tries IP providers in order; returns { city, state, country } or null. */
export async function fetchIPLocation() {
  for (const p of IP_PROVIDERS) {
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 5000);
      const r = await fetch(p.url, { signal: ctrl.signal });
      clearTimeout(timer);
      if (!r.ok) continue;
      const result = p.parse(await r.json());
      if (result?.city) return result;
    } catch {}
  }
  return null;
}

// ─── Reverse geocode (coordinates → city/state/country) ──────────────────────

// Promise-keyed cache: same coords only ever trigger one Nominatim request
const _rgCache = new Map();

/**
 * Reverse-geocode a lat/lng pair.
 * Returns { city, state, country } or null.
 * Results are cached permanently for the lifetime of the page.
 */
export function reverseGeocode(lat, lng) {
  const key = `${parseFloat(lat).toFixed(4)},${parseFloat(lng).toFixed(4)}`;
  if (_rgCache.has(key)) return _rgCache.get(key); // returns the same Promise
  const promise = (async () => {
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 6000);
      const r = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`,
        { signal: ctrl.signal, headers: { 'User-Agent': 'KalKode-Shop-App' } }
      );
      clearTimeout(timer);
      if (!r.ok) return null;
      const d = await r.json();
      return {
        city:    d.address?.city || d.address?.town || d.address?.village || d.address?.county || null,
        state:   d.address?.state   || null,
        country: d.address?.country || null,
      };
    } catch { return null; }
  })();
  _rgCache.set(key, promise);
  return promise;
}

// ─── Item distance/location badge ────────────────────────────────────────────

export const US_STATE_ABBREVS = {
  Alabama:'AL',Alaska:'AK',Arizona:'AZ',Arkansas:'AR',California:'CA',
  Colorado:'CO',Connecticut:'CT',Delaware:'DE',Florida:'FL',Georgia:'GA',
  Hawaii:'HI',Idaho:'ID',Illinois:'IL',Indiana:'IN',Iowa:'IA',Kansas:'KS',
  Kentucky:'KY',Louisiana:'LA',Maine:'ME',Maryland:'MD',Massachusetts:'MA',
  Michigan:'MI',Minnesota:'MN',Mississippi:'MS',Missouri:'MO',Montana:'MT',
  Nebraska:'NE',Nevada:'NV','New Hampshire':'NH','New Jersey':'NJ',
  'New Mexico':'NM','New York':'NY','North Carolina':'NC','North Dakota':'ND',
  Ohio:'OH',Oklahoma:'OK',Oregon:'OR',Pennsylvania:'PA','Rhode Island':'RI',
  'South Carolina':'SC','South Dakota':'SD',Tennessee:'TN',Texas:'TX',
  Utah:'UT',Vermont:'VT',Virginia:'VA',Washington:'WA','West Virginia':'WV',
  Wisconsin:'WI',Wyoming:'WY','District of Columbia':'DC',
};

// "City, ST" for US locations (abbreviated), "City, Country" otherwise —
// pure city/state label, independent of distance (unlike formatItemLocation,
// which prefers distance for anything within 30 miles).
export function formatCityStateLabel({ city, state, country } = {}) {
  if (!city) return null;
  const isUS = !country || /^(united states|us|usa)$/i.test(country);
  if (isUS) {
    const abbr = state ? (US_STATE_ABBREVS[state] || state) : null;
    return abbr ? `${city}, ${abbr}` : city;
  }
  return country ? `${city}, ${country}` : city;
}

export function isCoordInUSA(lat, lng) {
  return (lat >= 24 && lat <= 50 && lng >= -125 && lng <= -66) ||   // contiguous
         (lat >= 54 && lat <= 72 && lng >= -180 && lng <= -130) ||  // Alaska
         (lat >= 18 && lat <= 23 && lng >= -180 && lng <= -154);    // Hawaii
}

/**
 * Returns the badge text for an item's location indicator.
 *
 * Reads from item fields saved by ShopPage (city, state, country, locationSource, address)
 * plus enrichment fields added by WelcomePage/ShopPublicView (formattedDistance, distanceInMiles).
 *
 * Rules:
 *  No formattedDistance               → '' (badge hidden)
 *  ≤30 mi away                        → "2.3 mi"
 *  >30 mi, stored city (non-IP)       → "Houston, TX" or "Addis Ababa, Ethiopia"
 *  >30 mi, no stored city             → parse from item.address string
 *  IP-sourced city fields are ignored (distance from shop address is used instead)
 *
 * NOTE: Never reads item.itemCity — that field is first-comma-split of the raw
 * address string and often contains a street name, not a city name.
 */
export function formatItemLocation(item) {
  const stateAbbr = (s) => (s ? (US_STATE_ABBREVS[s] || s) : null);

  // Items need a computed distance (from real coordinates or shop address fallback)
  if (!item.formattedDistance) return '';

  const distMi = parseFloat(item.distanceInMiles);

  // Local (≤30 mi) → show raw distance
  if (isNaN(distMi) || distMi <= 30) return item.formattedDistance;

  // Far (>30 mi) → prefer stored city/state, but skip IP-sourced city fields (may be wrong)
  const city = (item.locationSource !== 'ip' ? item.city : null) || null;
  if (city) {
    const isUS = !item.country || /^(united states|us|usa)$/i.test(item.country);
    if (isUS) {
      const s = stateAbbr(item.state);
      return s ? `${city}, ${s}` : city;
    }
    return item.country ? `${city}, ${item.country}` : city;
  }

  // 2c. Far, no stored city → parse from raw address string (legacy items)
  // ShopPage saves address field as 'address'; very old items may use 'location'.
  const addrStr = item.address || item.location || '';
  const parts = addrStr.split(',').map(p => p.trim()).filter(Boolean);

  // Single-part address (e.g. "Addis Ababa") — use it directly as the city label
  if (parts.length === 1 && !/^\d/.test(parts[0])) return parts[0];

  if (parts.length < 2) return item.formattedDistance;

  const lat = parseFloat(item.coordinates?.lat);
  const lng = parseFloat(item.coordinates?.lng);

  if (isCoordInUSA(lat, lng)) {
    // US: scan parts for a state token; the part immediately before it is the city
    for (let i = 1; i < parts.length; i++) {
      const m = parts[i].match(/^([A-Z]{2})(\s+\d{5}(-\d{4})?)?$/);
      if (m) return `${parts[i - 1]}, ${m[1]}`;
      if (US_STATE_ABBREVS[parts[i]]) return `${parts[i - 1]}, ${US_STATE_ABBREVS[parts[i]]}`;
    }
    // US but no state token found: show last two parts
    return `${parts[parts.length - 2]}, ${parts[parts.length - 1]}`;
  } else {
    // Foreign (or no valid coords): last comma-part is the country, city is before it
    const last = parts[parts.length - 1];
    const parsedCity = parts.length >= 3 ? parts[parts.length - 2] : parts[0];
    if (!/^\d/.test(last)) return `${parsedCity}, ${last}`;
    // Last part is a postal code — drop it, show just the city
    return parsedCity;
  }
}

